import { type KeyboardEvent, useRef } from 'react'

/**
 * Arrow-key movement inside a tablist.
 *
 * Tabs use a roving tabindex: one Tab press reaches the group, then the arrow
 * keys move within it. Leaving every tab in the page's tab order would cost a
 * keyboard user one press per tab to walk past a control they did not want.
 *
 * Selection follows focus, which is what WAI-ARIA prescribes for tabs whose
 * panel is already rendered — arrowing to a tab shows it, with no Enter step.
 */
export function useRovingTabs(
  options: readonly { value: string }[],
  value: string,
  onChange: (next: string) => void,
) {
  const tabs = useRef<Record<string, HTMLElement | null>>({})

  const select = (index: number) => {
    const next = options[index]
    if (!next) return
    onChange(next.value)
    tabs.current[next.value]?.focus()
  }

  const step = (delta: number) => {
    const current = options.findIndex((option) => option.value === value)
    if (current < 0) return
    select((current + delta + options.length) % options.length)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      step(1)
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      step(-1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      select(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      select(options.length - 1)
    }
  }

  /** Callback ref factory — the map is what lets `select` move focus. */
  const register = (tabValue: string) => (element: HTMLElement | null) => {
    tabs.current[tabValue] = element
  }

  return { onKeyDown, register }
}
